import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LocalizeContextProps, withLocalize } from "react-localize-redux";
import { makeStyles, tokens } from "@fluentui/react-components";
import { ArrowClockwise16Regular, ArrowClockwise20Regular } from "@fluentui/react-icons";
import { Link } from "@fluentui/react-components";
import { SingleSelectTagPicker, TagData } from "../../_base/SingleSelectTagPicker";
import { useSuggestedProjects } from "../../../hooks/useSuggestedProjects";
import { IProjectData } from "../../../models/shared/IProjectData";
import { Logger } from "../../../services/Logger";
import { SmartFilingManagerFluent9 } from "../../../services/SmartFiling/SmartFilingManagerFluent9";
import { MailboxItem } from "../../../services/OfficeWrapper";
import { ITag } from "office-ui-fabric-react";
import { ExtendedITag } from "../../fileMultipleEmail/FileMultipleEmailComponent";

const useStyles = makeStyles({
    root: {
        display: "flex",
        flexDirection: "column",
    },
    container: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
    },
    subfolderContent: {
        display: "inline-flex",
        alignItems: "center",
    },
    link: {
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        marginLeft: "auto",
        fontSize: tokens.fontSizeBase200,
    },
});

export interface PaginatedProjectPickerFluent9Props extends LocalizeContextProps {
    logger: Logger;
    smartFilingManager: SmartFilingManagerFluent9;
    mailboxItem: MailboxItem | null;
    disabled: boolean;
    projects: ExtendedITag<string>[];
    myProjects?: ExtendedITag<string>[];
    onProjectSelected: (project: ITag | null) => void;
    isLoadingProjects: boolean;
    isLoadingRemainingProjects?: boolean;
    isReloadDisabled?: boolean;
    onRefresh: () => void;
    theme: string;
    reloadToken?: number;
    showAllGlobalProjects: boolean;
    reloadIconSize?: 16 | 20;
    subfolderToggleContent?: React.ReactNode;
    /** Rendered full-width below the subfolder toggle / reload projects row (e.g. the subfolder picker field). */
    subfolderPickerContent?: React.ReactNode;
}

const SUGGESTED_GROUP_KEY = "suggested";
const MY_PROJECTS_GROUP_KEY = "my";
const MORE_PROJECTS_GROUP_KEY = "more";
const MAX_SUGGESTED_PROJECTS = 3;
const MY_PROJECTS_DISPLAY_LIMIT = 25;
const MAX_DROPDOWN_ITEMS = 25;

export const PaginatedProjectPickerFluent9 = withLocalize((props: PaginatedProjectPickerFluent9Props) => {
    const styles = useStyles();
    const ReloadIcon = props.reloadIconSize === 16 ? ArrowClockwise16Regular : ArrowClockwise20Regular;
    const translationsReady = !!props.activeLanguage;
    const [query, setQuery] = useState("");
    const [selectedProject, setSelectedProject] = useState<ITag | null>(null);

    const myProjectsAsIProjectData: IProjectData[] = useMemo(
        () =>
            (props.myProjects ?? []).map((p) => ({
                name: p.name,
                displayName: p.name,
                nrn: p.key as string,
                autoFileMailboxFolderName: p.additionalValue ?? p.name,
            })),
        [props.myProjects]
    );

    const { suggestedProjects } = useSuggestedProjects({
        smartFilingManager: props.smartFilingManager,
        myProjects: myProjectsAsIProjectData,
        logger: props.logger,
        reloadToken: props.reloadToken,
    });

    // Auto-select first suggested project
    const autoSelectedRef = useRef(false);

    useEffect(() => {
        if (props.reloadToken !== undefined && props.reloadToken > 0) {
            autoSelectedRef.current = false;
            setSelectedProject(null);
            props.onProjectSelected(null);
        }
    }, [props.reloadToken]);

    useEffect(() => {
        if (autoSelectedRef.current || selectedProject || query.length > 0) return;
        if (suggestedProjects.length > 0) {
            const first = suggestedProjects[0];
            const tag: ITag = { key: first.nrn, name: first.displayName };
            setSelectedProject(tag);
            props.onProjectSelected(tag);
            autoSelectedRef.current = true;
        }
    }, [suggestedProjects]);

    const suggestedLabel = props.translate("SHARED.SUGGESTED_PROJECTS.SUGGESTED_PROJECTS_TITLE").toString();
    const myProjectsLabel = props.translate("SHARED.MY_PROJECTS").toString();
    const moreProjectsLabel = props.translate("FILE_MULTIPLE_EMAIL.USE_SEARCH").toString();

    const options = useMemo(() => {
        const trimmedLower = query.trim().toLowerCase();
        const isSearching = trimmedLower.length > 0;
        const result: TagData[] = [];

        // Suggested projects (always shown, client-filtered when searching, capped at 3 when not searching)
        const filteredSuggested = isSearching
            ? suggestedProjects.filter((p) => p.displayName.toLowerCase().includes(trimmedLower))
            : suggestedProjects.slice(0, MAX_SUGGESTED_PROJECTS);

        filteredSuggested.forEach((p) => {
            result.push({
                key: p.nrn,
                label: p.displayName,
                group: { key: SUGGESTED_GROUP_KEY, label: suggestedLabel },
            });
        });

        const suggestedNrns = new Set(suggestedProjects.map((p) => p.nrn));
        const myProjectKeys = new Set((props.myProjects ?? []).map((p) => p.key));

        // My Projects: always shown (filtered when searching)
        const myProjectsList = (props.myProjects ?? []).filter((p) => !suggestedNrns.has(p.key as string));
        const filteredMyProjects = isSearching
            ? myProjectsList.filter((p) => p.name.toLowerCase().includes(trimmedLower))
            : myProjectsList;

        filteredMyProjects.slice(0, MY_PROJECTS_DISPLAY_LIMIT).forEach((p) => {
            result.push({
                key: p.key as string,
                label: p.name,
                group: { key: MY_PROJECTS_GROUP_KEY, label: myProjectsLabel },
            });
        });

        // Global projects: only shown when user is searching AND showAllGlobalProjects is enabled
        if (isSearching && props.showAllGlobalProjects) {
            const globalProjects = props.projects.filter(
                (p) => !suggestedNrns.has(p.key as string) && !myProjectKeys.has(p.key)
            );
            const filteredGlobal = globalProjects.filter((p) => p.name.toLowerCase().includes(trimmedLower));

            filteredGlobal.slice(0, MAX_DROPDOWN_ITEMS - result.length).forEach((p) => {
                result.push({
                    key: p.key as string,
                    label: p.name,
                    group: { key: MORE_PROJECTS_GROUP_KEY, label: moreProjectsLabel },
                });
            });
        }

        const maxItems = isSearching ? MAX_DROPDOWN_ITEMS : MAX_SUGGESTED_PROJECTS + MY_PROJECTS_DISPLAY_LIMIT;
        return result.slice(0, maxItems);
    }, [
        query,
        suggestedProjects,
        props.projects,
        props.myProjects,
        props.showAllGlobalProjects,
        suggestedLabel,
        myProjectsLabel,
        moreProjectsLabel,
    ]);

    const selectedPickerOption = useMemo(() => {
        if (selectedProject) {
            return {
                key: selectedProject.key as string,
                label: selectedProject.name,
            };
        }
        return undefined;
    }, [selectedProject]);

    const handleQueryChange = useCallback((value: string) => {
        setQuery(value);
    }, []);

    const onOptionSelect = useCallback(
        (data?: TagData) => {
            if (!data) {
                setQuery("");
                setSelectedProject(null);
                props.onProjectSelected(null);
                return;
            }
            const tag: ITag = { key: data.key, name: data.label };
            setSelectedProject(tag);
            props.onProjectSelected(tag);
        },
        [props.onProjectSelected]
    );

    const placeholder = useMemo(() => {
        if (props.isLoadingProjects) {
            return props.translate("SHARED.SUGGESTED_PROJECTS.LOADING_PROJECTS").toString();
        }
        return props.translate("SHARED.SUGGESTED_PROJECTS.PLACEHOLDER").toString();
    }, [props.isLoadingProjects, props.translate]);

    const headerMessage = useMemo(() => {
        if (query.trim().length > 0) return undefined;
        const myProjectsCount = (props.myProjects ?? []).length;
        if (
            myProjectsCount > MY_PROJECTS_DISPLAY_LIMIT ||
            props.showAllGlobalProjects ||
            props.isLoadingRemainingProjects
        ) {
            return props.translate("FILE_MULTIPLE_EMAIL.USE_SEARCH").toString();
        }
        return undefined;
    }, [query, props.myProjects, props.showAllGlobalProjects, props.isLoadingRemainingProjects, props.translate]);

    if (!translationsReady) {
        return <div className={styles.root} data-testid="paginated-project-picker-container" />;
    }

    return (
        <div className={styles.root} data-testid="paginated-project-picker-container">
            <SingleSelectTagPicker
                label={props.translate("SHARED.SUGGESTED_PROJECTS.LABEL").toString()}
                name="paginated-project"
                options={options}
                selectedOption={selectedPickerOption}
                required
                disabled={props.disabled || props.isLoadingProjects}
                statusMessage={
                    props.isLoadingRemainingProjects ? props.translate("PROJECT_PICKER.PART_4").toString() : undefined
                }
                headerMessage={headerMessage}
                placeholder={placeholder}
                noOptionsLabel={props.translate("SHARED.SUGGESTED_PROJECTS.NO_PROJECTS").toString()}
                limitExceededLabel={props.translate("SHARED.FIELD.PROJECT_LIMIT_EXCEEDED.LABEL").toString()}
                onOptionSelect={onOptionSelect}
                onQueryChange={handleQueryChange}
                keepQueryOnClose
                limit={MAX_SUGGESTED_PROJECTS + MY_PROJECTS_DISPLAY_LIMIT}
            />
            <div className={styles.container}>
                {props.subfolderToggleContent ? (
                    <div className={styles.subfolderContent}>{props.subfolderToggleContent}</div>
                ) : null}
                <Link
                    data-testid="paginated-picker-reload-projects-button"
                    onClick={props.onRefresh}
                    className={styles.link}
                    disabled={props.isReloadDisabled ?? (props.isLoadingProjects || !!props.isLoadingRemainingProjects)}
                >
                    <ReloadIcon />
                    {props.translate("SEND_AND_FILE_NEW.RELOAD_PROJECTS")}
                </Link>
            </div>
            {props.subfolderPickerContent ?? null}
        </div>
    );
});

export default PaginatedProjectPickerFluent9;
